import { z } from 'zod';
import { SalesDescriptionSuggestionSchema } from './salesDescription.js';

/**
 * Actuator-sizing (MVP: actuator). Referentiedata uit de Air Torque-catalogus + Pekos-ratio's,
 * een deterministische selectie (SizingService) en drie ingangen: invullen (compute), valideren
 * (ValidationService) en het opzoeken van passende Unit4-actuator-artikelen. Zie het plan in
 * ~/.claude/plans en de projectmemory `airtorque-actuator-sizing-excel`.
 */

/** Werking van de actuator voor de sizing: dubbelwerkend, of veerretour die sluit (FTC) of opent (FTO). */
export const SizingActionSchema = z.enum(['double_acting', 'ftc', 'fto']);
export type SizingAction = z.infer<typeof SizingActionSchema>;

/** D = dubbelwerkend, S = veerretour (single acting). */
export const ActuatorActionSchema = z.enum(['D', 'S']);
export type ActuatorAction = z.infer<typeof ActuatorActionSchema>;

/** Fase van een veerretour-torquewaarde; NULL voor dubbelwerkend. */
export const TorquePhaseSchema = z.enum(['air_start', 'air_end', 'spring_start', 'spring_end']);
export type TorquePhase = z.infer<typeof TorquePhaseSchema>;

// ---------- Sizing-invoer op het product (products.sizing_input JSONB) ----------

/**
 * Opt-in invoerblok op het product. Alle velden nullable zodat een half ingevuld blok bewaard kan
 * worden; de compute/validatie behandelt ontbrekende velden als "onvolledig".
 */
export const SizingInputSchema = z.object({
  /** Basis klep-torque (BTO) in Nm */
  valve_torque: z.number().positive().nullable().optional().default(null),
  /** Stuurdruk in bar; moet een cataloguswaarde zijn (2,5 / 3 / 3,5 / 4 / 4,2 / 4,5 / 5 / 5,5 / 6 / 7 / 8) */
  supply_pressure: z.number().positive().nullable().optional().default(null),
  /** Minimale veiligheidsfactor (≥ 1) */
  safety_factor: z.number().min(1).nullable().optional().default(null),
  /** Zitting-/spindelafdichtingsfactor (≥ 1) */
  sealing_factor: z.number().min(0).nullable().optional().default(null),
  action: SizingActionSchema.nullable().optional().default(null),
  /** Sleutel naar sizing_valve_type (ratio's); NULL = standaardratio's */
  valve_type: z.string().max(60).nullable().optional().default(null),
  /** Optionele voorkeur/filter op veerpakket (bv. "S 12"); NULL = geen voorkeur */
  spring_pack: z.string().max(20).nullable().optional().default(null),
});
export type SizingInput = z.infer<typeof SizingInputSchema>;

/** Toegestane keuzes voor de sizing-invoer, afgeleid uit de referentiedata (`GET /api/sizing/options`). */
export const SizingOptionsSchema = z.object({
  supply_pressures: z.array(z.number()),
  valve_types: z.array(z.string()),
  spring_packs: z.array(z.string()),
});
export type SizingOptions = z.infer<typeof SizingOptionsSchema>;

export type SizingInputField = Exclude<keyof SizingInput, 'action'>;

const fmtNumber = (n: number) => String(n).replace('.', ',');
const sameText = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Controleert de ingevulde sizing-waarden (BugHerd #1442): alles moet precies kloppen met de catalogus,
 * anders rekent de sizing stilletjes met iets anders (bv. een onbekend kleptype → standaardratio's).
 * Lege velden zijn "onvolledig", geen fout. Gedeeld door het formulier (direct bij invoer) en de
 * validatie (blokkeert opslaan in Unit4). Zonder `options` alleen de bereikcontroles.
 */
export function sizingInputProblems(
  input: SizingInput | null | undefined,
  options: SizingOptions | null,
): Partial<Record<SizingInputField, string>> {
  const out: Partial<Record<SizingInputField, string>> = {};
  if (!input) return out;
  if (input.valve_torque != null && !(input.valve_torque > 0))
    out.valve_torque = 'Klep-torque moet groter dan 0 Nm zijn';
  if (input.safety_factor != null && !(input.safety_factor >= 1))
    out.safety_factor = 'Veiligheidsfactor moet minimaal 1 zijn';
  if (input.sealing_factor != null && !(input.sealing_factor >= 1))
    out.sealing_factor = 'Afdichtingsfactor moet minimaal 1 zijn';
  if (!options) return out;
  const pressure = input.supply_pressure;
  if (pressure != null && !options.supply_pressures.some((p) => Math.abs(p - pressure) < 0.01))
    out.supply_pressure = `Stuurdruk ${fmtNumber(pressure)} bar staat niet in de catalogus (${options.supply_pressures.map(fmtNumber).join(' / ')} bar)`;
  const valveType = input.valve_type?.trim();
  if (valveType && !options.valve_types.some((v) => sameText(v, valveType)))
    out.valve_type = `Kleptype "${valveType}" is onbekend; kies er een uit de lijst (leeg = standaardratio's)`;
  const springPack = input.spring_pack?.trim();
  if (
    springPack &&
    input.action !== 'double_acting' &&
    !options.spring_packs.some((s) => sameText(s, springPack))
  )
    out.spring_pack = `Veerpakket "${springPack}" staat niet in de catalogus`;
  return out;
}

// ---------- Referentiedata: actuator-catalogus ----------

/** Actuator-identiteit + brug naar Unit4 (actuator_catalog). */
export const ActuatorCatalogSchema = z.object({
  id: z.number().int(),
  model: z.string(),
  action: ActuatorActionSchema,
  square_mm: z.number().nullable(),
  iso_flange: z.string().nullable(),
  /** Unit4-productcode van dit actuator-artikel (voor herkenning en koppelen) */
  unit4_code: z.string().nullable(),
  unit_code: z.string().nullable(),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ActuatorCatalog = z.infer<typeof ActuatorCatalogSchema>;

export const ActuatorCatalogBodySchema = z.object({
  model: z.string().trim().min(1).max(20),
  action: ActuatorActionSchema,
  square_mm: z.number().int().positive().nullable().optional(),
  iso_flange: z.string().trim().max(40).nullable().optional(),
  unit4_code: z.string().trim().max(30).nullable().optional(),
  unit_code: z.string().trim().max(10).nullable().optional(),
  notes: z.string().trim().max(500).nullable().optional(),
});
export type ActuatorCatalogBody = z.infer<typeof ActuatorCatalogBodySchema>;

// ---------- Referentiedata: torquerijen ----------

/** Eén torquewaarde uit de catalogus (dubbelwerkend of veerretour). */
export const ActuatorTorqueSchema = z.object({
  id: z.number().int(),
  model: z.string(),
  action: ActuatorActionSchema,
  /** Veerpakket (bv. "S 07"); NULL voor dubbelwerkend */
  spring_pack: z.string().nullable(),
  supply_pressure: z.number(),
  /** NULL voor dubbelwerkend; anders de fase van de waarde */
  phase: TorquePhaseSchema.nullable(),
  torque: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ActuatorTorque = z.infer<typeof ActuatorTorqueSchema>;

export const ActuatorTorqueBodySchema = z.object({
  model: z.string().trim().min(1).max(20),
  action: ActuatorActionSchema,
  spring_pack: z.string().trim().max(20).nullable().optional(),
  // 0 is toegestaan voor de drukonafhankelijke veerslag-rijen (phase spring_start/spring_end)
  supply_pressure: z.number().nonnegative(),
  phase: TorquePhaseSchema.nullable().optional(),
  torque: z.number().nonnegative(),
});
export type ActuatorTorqueBody = z.infer<typeof ActuatorTorqueBodySchema>;

// ---------- Referentiedata: kleptype-ratio's ----------

/** Per kleptype de BTO/BTC/ETO/ETC-ratio's t.o.v. de basis-torque (sizing_valve_type). */
export const SizingValveTypeSchema = z.object({
  id: z.number().int(),
  valve_type: z.string(),
  bto_ratio: z.number(),
  btc_ratio: z.number(),
  eto_ratio: z.number(),
  etc_ratio: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type SizingValveType = z.infer<typeof SizingValveTypeSchema>;

export const SizingValveTypeBodySchema = z.object({
  valve_type: z.string().trim().min(1).max(60),
  bto_ratio: z.number().positive(),
  btc_ratio: z.number().positive(),
  eto_ratio: z.number().positive(),
  etc_ratio: z.number().positive(),
});
export type SizingValveTypeBody = z.infer<typeof SizingValveTypeBodySchema>;

// ---------- Compute (invullen) ----------

export const SizingComputeBodySchema = z.object({
  product_id: z.number().int().positive(),
  /** Optioneel: invoer meesturen i.p.v. van het product lezen (bv. live in het formulier) */
  input: SizingInputSchema.nullable().optional(),
});
export type SizingComputeBody = z.infer<typeof SizingComputeBodySchema>;

/** Het gekozen actuatormodel uit de berekening. */
export const SizingRecommendationSchema = z.object({
  model: z.string(),
  action: ActuatorActionSchema,
  spring_pack: z.string().nullable(),
  iso_flange: z.string().nullable(),
  square_mm: z.number().nullable(),
});
export type SizingRecommendation = z.infer<typeof SizingRecommendationSchema>;

/** Resultaat van de sizing-berekening. */
export const SizingResultSchema = z.object({
  ok: z.boolean(),
  required_torque: z.number().nullable(),
  recommended: SizingRecommendationSchema.nullable(),
  delivered_torque: z.number().nullable(),
  realized_safety_factor: z.number().nullable(),
  /** Toelichting (bv. "geen actuator gevonden die voldoet") */
  message: z.string(),
});
export type SizingResult = z.infer<typeof SizingResultSchema>;

/** Compute-respons: het resultaat + een suggestion-object dat het omschrijvingsformulier invult. */
export const SizingComputeResponseSchema = z.object({
  result: SizingResultSchema,
  suggestion: SalesDescriptionSuggestionSchema,
});
export type SizingComputeResponse = z.infer<typeof SizingComputeResponseSchema>;

// ---------- Unit4-kandidaten ----------

export const ActuatorCandidateSchema = z.object({
  code: z.string(),
  description: z.string().nullable(),
  unit_code: z.string().nullable(),
  /** 'cache' = uit de catalogus-cache, 'catalog' = actuator_catalog-mapping */
  source: z.enum(['catalog', 'cache']),
});
export type ActuatorCandidate = z.infer<typeof ActuatorCandidateSchema>;

export const ActuatorCandidatesResponseSchema = z.object({
  recommended: SizingRecommendationSchema.nullable(),
  candidates: z.array(ActuatorCandidateSchema),
});
export type ActuatorCandidatesResponse = z.infer<typeof ActuatorCandidatesResponseSchema>;

// ---------- Excel-import ----------

export const SizingImportRowSchema = z.object({
  sheet: z.string(),
  action: z.enum(['insert', 'update', 'unchanged', 'skip', 'fix']),
  detail: z.string(),
});
export const SizingImportReportSchema = z.object({
  applied: z.boolean(),
  catalog: z.number().int(),
  torques: z.number().int(),
  valveTypes: z.number().int(),
  rows: z.array(SizingImportRowSchema),
  warnings: z.array(z.string()),
});
export type SizingImportRow = z.infer<typeof SizingImportRowSchema>;
export type SizingImportReport = z.infer<typeof SizingImportReportSchema>;
